import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Avatar,
  CardMedia,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Inventory2Outlined as StorageBoxIcon,
  Place as PlaceIcon,
  Clear as ClearFilterIcon,
  ChatBubbleOutline as MessagesIcon,
  Print as PrintIcon,
  LocalShipping as ShippingIcon,
  MailOutline as AccountlessMailIcon,
} from '@mui/icons-material';
import { useConversationsListQuery } from '../hooks/useConversationsListQuery';
import { invalidateFoundObjectStats } from '../lib/messagingQueryCache';
import { useMutation, useQueryClient, keepPreviousData, useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { FoundObject, Category } from '../types/foundObject';
import FoundObjectDialog from '../components/FoundObjectDialog';
import AccountlessRestitutionDialog from '../components/AccountlessRestitutionDialog';
import { muiDialogTransitionProps, releaseFocus } from '../utils/dialogA11y';
import FoundObjectMessagesWorkspace from '../components/FoundObjectMessagesWorkspace';
import {
  FilterState,
  countActiveFoundObjectFilters,
} from '../components/FoundObjectsFilters';
import FoundObjectsTopFilters from '../components/FoundObjectsTopFilters';
import FoundObjectCardAttributes from '../components/FoundObjectCardAttributes';
import FormattedReferenceCode from '../components/FormattedReferenceCode';
import { getProviderPlaceDisplayColor } from '../utils/providerPlaceColor';
import CategoryIcon from '../components/CategoryIcon';
import { getAnnouncementThumbUrl } from '../utils/imageUrl';
import { formatFrenchDateAtTime, formatFrenchDateOnly } from '../utils/formatDateTimeFr';
import {
  buildFoundObjectUnreadCountMap,
  getConversationsForFoundObject,
  getFoundObjectUnreadLabel,
} from '../utils/foundObjectConversation';
import { resolveFoundObjectEtiquetteUrl } from '../utils/colissimoLabel';
import { DASHBOARD_BRAND_PRIMARY } from '../theme/dashboardBrand';
import { shouldRestoreFoundObjectCreateDraft } from '../utils/foundObjectCreateDraft';
import { useProviderInfo } from '../hooks/useProviderInfo';
import { isCentralPlaceStorageMode, isSplitPlaceStorageMode } from '../utils/placeStorageMode';
const shipButtonSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  color: '#1565c0',
  '& .MuiButton-startIcon': { color: '#1565c0' },
  '&:hover': { bgcolor: 'rgba(21, 101, 192, 0.08)' },
};

const CARD_ACTION_BLACK = '#212B36';
const restitutionButtonSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  color: DASHBOARD_BRAND_PRIMARY,
  '& .MuiButton-startIcon': { color: DASHBOARD_BRAND_PRIMARY },
  '&:hover': { bgcolor: 'rgba(81, 190, 238, 0.08)' },
};
const editButtonSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  color: CARD_ACTION_BLACK,
  '& .MuiButton-startIcon': { color: CARD_ACTION_BLACK },
  '&:hover': { bgcolor: 'rgba(33, 43, 54, 0.06)' },
};

interface FoundObjectsPageProps {
  title?: string;
  lockedStatus?: string;
}

function parseApiDateTime(raw?: string | null): Date | null {
  if (!raw) return null;
  const normalized = String(raw).includes('T') ? String(raw) : String(raw).replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Texte « Restitué le … par … » uniquement si statut restitué ; absent après annulation. */
function formatRestitutionSentence(object: FoundObject): string | null {
  if (object.status !== 'claimed') return null;
  const by = (object.claimantName ?? object.claimant_name ?? '').trim();
  const raw =
    object.claimedAt ?? object.claimed_at ?? object.updatedAt ?? object.updated_at ?? object.createdAt;
  const d = parseApiDateTime(raw);
  const datePart =
    d != null
      ? d.toLocaleString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;
  if (datePart && by) return `Restitué le ${datePart} par ${by}`;
  if (datePart) return `Restitué le ${datePart}`;
  if (by) return `Restitué par ${by}`;
  return null;
}

function getFoundObjectPlaceName(object: FoundObject, preferStoragePlace: boolean): string {
  const storagePlaceName =
    object.storage_box?.provider_item?.name?.trim() ||
    '';
  const foundPlaceName =
    object.found_provider_item?.display_name?.trim() ||
    object.found_provider_item?.name?.trim() ||
    '';
  if (preferStoragePlace) {
    return storagePlaceName || foundPlaceName;
  }
  return foundPlaceName || storagePlaceName;
}

function getFoundObjectPlaceColor(object: FoundObject, preferStoragePlace: boolean): string {
  const storagePlace = object.storage_box?.provider_item ?? null;
  const foundPlace = object.found_provider_item ?? null;
  const place = preferStoragePlace
    ? storagePlace || foundPlace
    : foundPlace || storagePlace;
  return getProviderPlaceDisplayColor(place);
}

const cardPhotoOverlayIconSx = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(255,255,255,0.92)',
  color: 'text.secondary',
  flexShrink: 0,
} as const;

const cardPhotoOverlayLabelSx = {
  color: 'common.white',
  textShadow: '0 1px 2px rgba(0,0,0,0.6)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const FoundObjectsPage: React.FC<FoundObjectsPageProps> = ({ title, lockedStatus }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  const createParam = searchParams.get('create');
  const foundObjectDetailParam = searchParams.get('found_object');
  const detailObjectId = foundObjectDetailParam ? parseInt(foundObjectDetailParam, 10) : NaN;
  const [detailConversationId, setDetailConversationId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingObject, setEditingObject] = useState<FoundObject | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState<FoundObject | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [toShipConfirmObject, setToShipConfirmObject] = useState<FoundObject | null>(null);
  const [printingObjectId, setPrintingObjectId] = useState<number | null>(null);
  const [printLabelError, setPrintLabelError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [handoverCongratsOpen, setHandoverCongratsOpen] = useState(false);
  const [accountlessObject, setAccountlessObject] = useState<FoundObject | null>(null);
  const [accountlessSuccess, setAccountlessSuccess] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    referenceCode: '',
    category: '',
    status: lockedStatus ?? '',
    dateFrom: '',
    dateTo: '',
    createdFrom: '',
    createdTo: '',
    location: '',
    attributes: {},
    provider_event_id: '',
    storage_box_id: '',
    provider_item_id: '',
  });
  /** Pages livraison : pas de Restitution / Modifier / Supprimer sur les cards */
  const hideCardManagementActions =
    lockedStatus === 'to_ship' || lockedStatus === 'in_transit';

  const queryClient = useQueryClient();
  const { data: providerInfo } = useProviderInfo();
  const preferStoragePlace = !isSplitPlaceStorageMode(providerInfo) && !isCentralPlaceStorageMode(providerInfo);
  const accountlessRestitutionEnabled =
    (providerInfo?.settings as Record<string, unknown> | undefined)?.accountless_restitution_enabled === true;

  const { data: categoriesList } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => (response.data?.categories || []) as Category[],
  });

  const getCategoryForType = (objectType: string): Category | undefined =>
    categoriesList?.find((c) => c.name === objectType);

  const { data: detailFoundObjectCached } = useQuery({
    queryKey: ['found-object', detailObjectId],
    queryFn: () => apiService.getFoundObject(detailObjectId).then((r) => r.data.data),
    enabled: Number.isFinite(detailObjectId) && detailObjectId > 0,
  });

  const { data: conversationsData } = useConversationsListQuery({ poll: false });

  const unreadByFoundObjectId = useMemo(
    () => buildFoundObjectUnreadCountMap(conversationsData?.conversations ?? []),
    [conversationsData]
  );

  const { data, isPending, error } = useQuery({
    queryKey: ['found-objects', filters],
    queryFn: () =>
      apiService.getFoundObjects({
        ...filters,
        referenceCode: filters.referenceCode || undefined,
        createdFrom: filters.createdFrom || undefined,
        createdTo: filters.createdTo || undefined,
        provider_event_id: filters.provider_event_id
          ? parseInt(filters.provider_event_id, 10)
          : undefined,
        storage_box_id: filters.storage_box_id
          ? parseInt(filters.storage_box_id, 10)
          : undefined,
        provider_item_id: filters.provider_item_id
          ? parseInt(filters.provider_item_id, 10)
          : undefined,
        limit: 100,
      }),
    select: (response) => response.data,
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteFoundObject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      void invalidateFoundObjectStats(queryClient);
      setDeleteDialogOpen(false);
      setObjectToDelete(null);
      setDeleteError(null);
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Suppression impossible.';
      setDeleteError(message);
    },
  });

  const objectHasConversations = (objectId: number): boolean =>
    getConversationsForFoundObject(conversationsData?.conversations ?? [], objectId).length > 0;

  const markInTransitMutation = useMutation({
    mutationFn: (id: number) => apiService.updateFoundObject(id, { status: 'in_transit' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      queryClient.invalidateQueries({ queryKey: ['found-object'] });
      void invalidateFoundObjectStats(queryClient);
    },
  });

  const markToShipMutation = useMutation({
    mutationFn: (id: number) => apiService.updateFoundObject(id, { status: 'to_ship' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      queryClient.invalidateQueries({ queryKey: ['found-object'] });
      void invalidateFoundObjectStats(queryClient);
      setToShipConfirmObject(null);
    },
  });

  const handlePrintLabel = async (object: FoundObject) => {
    setPrintLabelError(null);
    setPrintingObjectId(object.id);
    try {
      const url = await resolveFoundObjectEtiquetteUrl(
        object.id,
        conversationsData?.conversations ?? [],
        (id) => apiService.getConversation(id, 1, 50)
      );
      if (!url) {
        setPrintLabelError(
          'Bon de livraison introuvable. Ouvrez Restitution pour imprimer depuis la conversation.'
        );
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      if (object.status === 'to_ship') {
        try {
          await markInTransitMutation.mutateAsync(object.id);
        } catch {
          setPrintLabelError(
            'Bon ouvert, mais le passage en « Parti en livraison » a échoué. Réessayez ou changez le statut manuellement.'
          );
        }
      }
    } catch {
      setPrintLabelError('Impossible de récupérer le bon de livraison.');
    } finally {
      setPrintingObjectId(null);
    }
  };

  const handleAdd = () => {
    releaseFocus();
    setEditingObject(null);
    setDialogOpen(true);
  };

  const handleEdit = (object: FoundObject) => {
    releaseFocus();
    setEditingObject(object);
    setDialogOpen(true);
  };

  /** Ouvre la fiche depuis un lien (ex. stockage : `/found-objects?open=123`). */
  useEffect(() => {
    if (!openParam || !String(openParam).trim()) return;
    const id = parseInt(String(openParam).trim(), 10);
    if (!Number.isFinite(id) || id <= 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('open');
          return next;
        },
        { replace: true }
      );
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiService.getFoundObject(id);
        if (cancelled) return;
        const fo = res.data?.data;
        if (fo) {
          setEditingObject(fo);
          setDialogOpen(true);
        }
      } catch {
        /* id invalide ou accès refusé */
      } finally {
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('open');
              return next;
            },
            { replace: true }
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openParam, setSearchParams]);

  useEffect(() => {
    if (createParam !== '1' && createParam !== 'true') return;
    setEditingObject(null);
    setDialogOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('create');
        return next;
      },
      { replace: true }
    );
  }, [createParam, setSearchParams]);

  /** Reload sur /found-objects sans ?create=1 (ex. kill caméra) : rouvrir si brouillon. */
  useEffect(() => {
    if (createParam === '1' || createParam === 'true') return;
    if (!shouldRestoreFoundObjectCreateDraft()) return;
    setEditingObject(null);
    setDialogOpen(true);
  }, [createParam]);

  useEffect(() => {
    setDetailConversationId(null);
  }, [foundObjectDetailParam]);

  const openObjectDetails = (id: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('found_object', String(id));
        next.delete('open');
        return next;
      },
      { replace: true }
    );
  };

  const stopCardClickPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const closeObjectDetails = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('found_object');
        return next;
      },
      { replace: true }
    );
    setDetailConversationId(null);
  };

  const handleHandoverConfirmed = (_userName: string) => {
    closeObjectDetails();
    setHandoverCongratsOpen(true);
  };

  const handleCloseCongrats = () => {
    releaseFocus();
    setHandoverCongratsOpen(false);
    if (lockedStatus !== 'claimed') {
      navigate('/returned-objects');
    }
  };

  const handleDelete = (object: FoundObject) => {
    releaseFocus();
    setDeleteError(null);
    if (objectHasConversations(object.id)) {
      setDeleteError(
        'Suppression impossible : cet objet a déjà une ou plusieurs discussions.'
      );
      setObjectToDelete(object);
      setDeleteDialogOpen(true);
      return;
    }
    setObjectToDelete(object);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    releaseFocus();
    setDeleteDialogOpen(false);
    setObjectToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = () => {
    if (objectToDelete) {
      deleteMutation.mutate(objectToDelete.id);
    }
  };

  const handleCloseDialog = () => {
    releaseFocus();
    setDialogOpen(false);
    setEditingObject(null);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      referenceCode: '',
      category: '',
      status: lockedStatus ?? '',
      dateFrom: '',
      dateTo: '',
      createdFrom: '',
      createdTo: '',
      location: '',
      attributes: {},
      provider_event_id: '',
      storage_box_id: '',
      provider_item_id: '',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'found':
        return 'success';
      case 'to_ship':
        return 'warning';
      case 'in_transit':
        return 'info';
      case 'claimed':
        return 'info';
      case 'disposed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'found':
        return 'Trouvé';
      case 'to_ship':
        return 'À expédier';
      case 'in_transit':
        return 'Parti en livraison';
      case 'claimed':
        return 'RESTITUÉ';
      case 'disposed':
        return 'Éliminé';
      default:
        return status;
    }
  };

  const foundObjects = data?.data?.found_objects || [];
  const total = data?.data?.total || 0;
  const activeFilterCount = countActiveFoundObjectFilters(filters, lockedStatus);

  return (
    <>
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={2}
      >
        <Box display="flex" alignItems="baseline" gap={2} flexWrap="wrap" justifyContent="space-between" flex={1}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.35rem', sm: '2rem' } }}>
            {title ?? 'Objets'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            {total} Résultat{total > 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={1}
          sx={{ alignSelf: { xs: 'stretch', sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}
          >
            Publier un objet
          </Button>
        </Box>
      </Box>

      <FoundObjectsTopFilters filters={filters} onFiltersChange={setFilters} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des objets trouvés: {error.message}
        </Alert>
      )}

      {accountlessSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAccountlessSuccess(null)}>
          {accountlessSuccess}
        </Alert>
      )}

      {activeFilterCount > 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          sx={{
            mb: 2,
            py: 1.5,
            px: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {activeFilterCount === 1
              ? '1 filtre actif — résultats filtrés.'
              : `${activeFilterCount} filtres actifs — résultats filtrés.`}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<ClearFilterIcon />}
            onClick={handleClearFilters}
          >
            Réinitialiser les filtres
          </Button>
        </Box>
      )}

      {isPending && !data ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320} sx={{ mb: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
      {/* Contrôles d'affichage */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="grid">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="list">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Liste des objets trouvés */}
      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {foundObjects.map((object) => {
            const cat = getCategoryForType(object.objectType);
            const thumbUrl = getAnnouncementThumbUrl(object.images, cat?.default_image_url);
            const restitutionLine = formatRestitutionSentence(object);
            const placeName = getFoundObjectPlaceName(object, preferStoragePlace);
            const placeColor = getFoundObjectPlaceColor(object, preferStoragePlace);
            const unreadMessages = unreadByFoundObjectId.get(object.id) ?? 0;
            const unreadLabel = getFoundObjectUnreadLabel(unreadMessages);
            return (
            <Grid item xs={12} sm={6} md={4} key={object.id}>
              <Card
                onClick={() => openObjectDetails(object.id)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {thumbUrl ? (
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={thumbUrl}
                      alt={object.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(15,23,42,0.04) 45%, rgba(15,23,42,0.66) 100%)',
                        pointerEvents: 'none',
                      }}
                    />
                    <Chip
                      label={getStatusLabel(object.status)}
                      color={getStatusColor(object.status) as any}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                      }}
                    />
                    {object.status !== 'claimed' && unreadMessages > 0 ? (
                      <Chip
                        icon={<MessagesIcon sx={{ fontSize: '16px !important' }} />}
                        label={unreadLabel}
                        size="small"
                        color="error"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          fontWeight: 700,
                          maxWidth: 'calc(100% - 120px)',
                          '& .MuiChip-icon': { color: 'inherit' },
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    ) : null}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 12,
                        right: 12,
                        bottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        minWidth: 0,
                      }}
                    >
                      {placeName ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                            flex: '1 1 0',
                          }}
                        >
                          <Box
                            sx={{
                              ...cardPhotoOverlayIconSx,
                              bgcolor: `${placeColor}e6`,
                              color: '#fff',
                            }}
                          >
                            <PlaceIcon fontSize="small" />
                          </Box>
                          <Typography variant="subtitle2" sx={cardPhotoOverlayLabelSx}>
                            {placeName}
                          </Typography>
                        </Box>
                      ) : null}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: 0,
                          flex: '1 1 0',
                        }}
                      >
                        <Box
                          sx={{
                            ...cardPhotoOverlayIconSx,
                            border: `2px solid ${placeColor}`,
                            color: placeColor,
                          }}
                        >
                          <StorageBoxIcon fontSize="small" />
                        </Box>
                        <Typography variant="subtitle2" sx={cardPhotoOverlayLabelSx}>
                          {object.storage_box?.name || 'Boite non renseignee'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : null}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ flex: '1 1 auto' }}>
                      {object.title}
                    </Typography>
                    {!thumbUrl && object.status !== 'claimed' && unreadMessages > 0 ? (
                      <Chip
                        icon={<MessagesIcon sx={{ fontSize: '16px !important' }} />}
                        label={unreadLabel}
                        size="small"
                        color="error"
                        sx={{ '& .MuiChip-icon': { color: 'inherit' } }}
                      />
                    ) : null}
                  </Box>
                  {object.reference_code && (
                    <FormattedReferenceCode
                      code={object.reference_code}
                      labelPrefix="Ref: "
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CategoryIcon
                      icon={cat?.icon}
                      customIconUrl={cat?.custom_icon_url}
                      customIconFilename={cat?.custom_icon_filename}
                      size="small"
                      color="primary.main"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {object.objectType}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Trouvé le {formatFrenchDateOnly(object.foundAt ?? object.found_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Créé le {formatFrenchDateAtTime(object.createdAt ?? object.created_at)}
                  </Typography>

                  {/* Attributs */}
                  <FoundObjectCardAttributes
                    objectAttributes={object.objectAttributes ?? object.object_attributes}
                    category={cat}
                  />

                  {restitutionLine && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {restitutionLine}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ flexWrap: 'wrap', gap: 0.5, px: 2, py: 1.5 }} onClick={stopCardClickPropagation}>
                  {object.status === 'to_ship' ? (
                    <Button
                      size="small"
                      startIcon={
                        printingObjectId === object.id || markInTransitMutation.isPending ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <PrintIcon />
                        )
                      }
                      onClick={() => void handlePrintLabel(object)}
                      disabled={printingObjectId === object.id || markInTransitMutation.isPending}
                      sx={shipButtonSx}
                    >
                      Imprimer le bon
                    </Button>
                  ) : null}
                  {object.status === 'in_transit' ? (
                    <>
                      <Button
                        size="small"
                        startIcon={
                          printingObjectId === object.id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <PrintIcon />
                          )
                        }
                        onClick={() => void handlePrintLabel(object)}
                        disabled={printingObjectId === object.id}
                        sx={shipButtonSx}
                      >
                        Imprimer le bon
                      </Button>
                      <Button
                        size="small"
                        startIcon={<ShippingIcon />}
                        onClick={() => setToShipConfirmObject(object)}
                        disabled={markToShipMutation.isPending}
                        sx={shipButtonSx}
                      >
                        Remettre à expédier
                      </Button>
                    </>
                  ) : null}
                  {!hideCardManagementActions ? (
                    <>
                      {object.status !== 'claimed' ? (
                      <Button
                        size="small"
                        startIcon={<MessagesIcon />}
                        onClick={() => openObjectDetails(object.id)}
                        variant="text"
                        sx={restitutionButtonSx}
                      >
                        Restitution
                      </Button>
                      ) : null}
                      {accountlessRestitutionEnabled && object.status === 'found' ? (
                        <Button
                          size="small"
                          startIcon={<AccountlessMailIcon />}
                          onClick={() => setAccountlessObject(object)}
                          variant="text"
                          sx={restitutionButtonSx}
                        >
                          Restitution sans compte
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(object)}
                        sx={editButtonSx}
                      >
                        Modifier
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(object)}
                        disabled={objectHasConversations(object.id)}
                        title={
                          objectHasConversations(object.id)
                            ? 'Impossible : des discussions existent sur cet objet'
                            : undefined
                        }
                      >
                        Supprimer
                      </Button>
                    </>
                  ) : null}
                </CardActions>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      ) : (
        <List>
          {foundObjects.map((object) => {
            const cat = getCategoryForType(object.objectType);
            const thumbUrl = getAnnouncementThumbUrl(object.images, cat?.default_image_url);
            const restitutionLine = formatRestitutionSentence(object);
            const unreadMessages = unreadByFoundObjectId.get(object.id) ?? 0;
            const unreadLabel = getFoundObjectUnreadLabel(unreadMessages);
            return (
            <ListItem
              key={object.id}
              divider
              alignItems="flex-start"
              onClick={() => openObjectDetails(object.id)}
              sx={{
                flexDirection: { xs: 'column', md: 'row' },
                pr: { xs: 2, md: 24 },
                cursor: 'pointer',
                borderRadius: 2,
                '&:hover': { bgcolor: 'action.hover' },
                '& .MuiListItemSecondaryAction-root': {
                  position: { xs: 'static', md: 'absolute' },
                  transform: { xs: 'none', md: undefined },
                  mt: { xs: 1, md: 0 },
                  ml: { xs: 6, md: 0 },
                  width: { xs: '100%', md: 'auto' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44, mt: { xs: 0.5, md: 0 } }}>
                <CategoryIcon
                  icon={cat?.icon}
                  customIconUrl={cat?.custom_icon_url}
                  customIconFilename={cat?.custom_icon_filename}
                  size="small"
                  color="primary.main"
                />
              </ListItemIcon>
              <Avatar
                sx={{ mr: { xs: 1, md: 2 }, width: 48, height: 48, flexShrink: 0 }}
                src={thumbUrl || undefined}
              >
                {object.title.charAt(0)}
              </Avatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box component="span">{object.title}</Box>
                    {object.status !== 'claimed' && unreadMessages > 0 ? (
                      <Chip
                        icon={<MessagesIcon sx={{ fontSize: '14px !important' }} />}
                        label={unreadLabel}
                        size="small"
                        color="error"
                        sx={{ height: 22, '& .MuiChip-icon': { color: 'inherit' } }}
                      />
                    ) : null}
                  </Box>
                }
                secondary={
                  <Box>
                    {object.reference_code && (
                      <FormattedReferenceCode
                        code={object.reference_code}
                        labelPrefix="Ref: "
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                    <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      Trouvé le {formatFrenchDateOnly(object.foundAt ?? object.found_at)}
                    </Box>
                    <Box component="div" sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.25 }}>
                      Créé le {formatFrenchDateAtTime(object.createdAt ?? object.created_at)}
                    </Box>

                    {/* Attributs */}
                    <FoundObjectCardAttributes
                      objectAttributes={object.objectAttributes ?? object.object_attributes}
                      category={cat}
                    />

                    {restitutionLine && (
                      <Box component="div" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                        {restitutionLine}
                      </Box>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction onClick={stopCardClickPropagation}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  {object.status === 'to_ship' ? (
                    <Button
                      size="small"
                      startIcon={
                        printingObjectId === object.id || markInTransitMutation.isPending ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <PrintIcon />
                        )
                      }
                      onClick={() => void handlePrintLabel(object)}
                      disabled={printingObjectId === object.id || markInTransitMutation.isPending}
                      sx={shipButtonSx}
                    >
                      Imprimer le bon
                    </Button>
                  ) : null}
                  {object.status === 'in_transit' ? (
                    <>
                      <Button
                        size="small"
                        startIcon={
                          printingObjectId === object.id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <PrintIcon />
                          )
                        }
                        onClick={() => void handlePrintLabel(object)}
                        disabled={printingObjectId === object.id}
                        sx={shipButtonSx}
                      >
                        Imprimer le bon
                      </Button>
                      <Button
                        size="small"
                        startIcon={<ShippingIcon />}
                        onClick={() => setToShipConfirmObject(object)}
                        disabled={markToShipMutation.isPending}
                        sx={shipButtonSx}
                      >
                        Remettre à expédier
                      </Button>
                    </>
                  ) : null}
                  {!hideCardManagementActions ? (
                    <>
                      {object.status !== 'claimed' ? (
                      <Button
                        size="small"
                        startIcon={<MessagesIcon />}
                        onClick={() => openObjectDetails(object.id)}
                        variant="text"
                        sx={restitutionButtonSx}
                      >
                        Restitution
                      </Button>
                      ) : null}
                      {accountlessRestitutionEnabled && object.status === 'found' ? (
                        <Button
                          size="small"
                          startIcon={<AccountlessMailIcon />}
                          onClick={() => setAccountlessObject(object)}
                          variant="text"
                          sx={restitutionButtonSx}
                        >
                          Restitution sans compte
                        </Button>
                      ) : null}
                      <Chip
                        label={getStatusLabel(object.status)}
                        color={getStatusColor(object.status) as any}
                        size="small"
                      />
                      <IconButton
                        edge="end"
                        onClick={() => handleEdit(object)}
                        size="small"
                        aria-label="Modifier"
                        sx={{ color: CARD_ACTION_BLACK }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(object)}
                        color="error"
                        size="small"
                        aria-label="Supprimer"
                        disabled={objectHasConversations(object.id)}
                        title={
                          objectHasConversations(object.id)
                            ? 'Impossible : des discussions existent sur cet objet'
                            : undefined
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  ) : (
                    <Chip
                      label={getStatusLabel(object.status)}
                      color={getStatusColor(object.status) as any}
                      size="small"
                    />
                  )}
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
            );
          })}
        </List>
      )}

      {foundObjects.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            Aucun objet trouvé
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Commencez par ajouter votre premier objet trouvé.
          </Typography>
        </Box>
      )}
        </>
      )}

      {/* Dialog pour ajouter/modifier un objet */}
      <FoundObjectDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        object={editingObject}
      />

      <AccountlessRestitutionDialog
        open={Boolean(accountlessObject)}
        object={accountlessObject}
        locationProviderId={providerInfo?.id}
        onClose={() => setAccountlessObject(null)}
        onSuccess={(updated) => {
          setAccountlessSuccess(
            'Proposition de livraison envoyée par e-mail. La personne pourra payer sans créer de compte.'
          );
          void queryClient.invalidateQueries({ queryKey: ['found-objects'] });
          if (updated?.id) {
            void queryClient.invalidateQueries({ queryKey: ['found-object', updated.id] });
          }
        }}
      />

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          {deleteError || (objectToDelete && objectHasConversations(objectToDelete.id)) ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {deleteError ||
                'Suppression impossible : cet objet a déjà une ou plusieurs discussions.'}
            </Alert>
          ) : (
            <Typography>
              Êtes-vous sûr de vouloir supprimer l&apos;objet &quot;{objectToDelete?.title}&quot; ?
              Cette action est irréversible.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>
            {deleteError || (objectToDelete && objectHasConversations(objectToDelete.id))
              ? 'Fermer'
              : 'Annuler'}
          </Button>
          {!(deleteError || (objectToDelete && objectHasConversations(objectToDelete.id))) && (
            <Button
              onClick={() => {
                releaseFocus();
                confirmDelete();
              }}
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(toShipConfirmObject)}
        onClose={() => !markToShipMutation.isPending && setToShipConfirmObject(null)}
        fullWidth
        maxWidth="xs"
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>Remettre à expédier</DialogTitle>
        <DialogContent>
          <DialogContentText>Confirmez que cet objet revient au statut « À expédier »</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setToShipConfirmObject(null)}
            disabled={markToShipMutation.isPending}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!toShipConfirmObject || markToShipMutation.isPending}
            onClick={() => {
              if (!toShipConfirmObject) return;
              markToShipMutation.mutate(toShipConfirmObject.id);
            }}
          >
            {markToShipMutation.isPending ? <CircularProgress size={20} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(printLabelError)}
        onClose={() => setPrintLabelError(null)}
        fullWidth
        maxWidth="xs"
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>Imprimer le bon</DialogTitle>
        <DialogContent>
          <DialogContentText>{printLabelError}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintLabelError(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Container>

      {Number.isFinite(detailObjectId) && detailObjectId > 0 && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            /* Sous les Dialog MUI (modal) pour que le formulaire d’édition reste au-dessus */
            zIndex: (theme) => theme.zIndex.modal - 1,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <FoundObjectMessagesWorkspace
              variant="fullscreen"
              foundObjectId={detailObjectId}
              selectedConversationId={detailConversationId}
              onSelectConversation={(id) => setDetailConversationId(id)}
              onHandoverConfirmed={handleHandoverConfirmed}
              onClose={closeObjectDetails}
              onRequestEditFoundObject={() => {
                const fo =
                  detailFoundObjectCached ??
                  (queryClient.getQueryData(['found-object', detailObjectId]) as FoundObject | undefined);
                if (fo) {
                  setEditingObject(fo);
                  setDialogOpen(true);
                }
              }}
            />
          </Box>
        </Box>
      )}
      <Dialog
        open={handoverCongratsOpen}
        onClose={handleCloseCongrats}
        fullWidth
        maxWidth="xs"
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>Félicitation !</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Tu viens de faire un Heureux ou une Heureuse ! 🎉
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseCongrats}>
            Je suis comme ça !
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FoundObjectsPage;